<template>
  <div class="wrapper">
    <div class="main visual-2 doc-profile-main">
      <Header />

      <!-- Page Loader -->
      <div v-if="pageLoading" class="page-loader">
        <div class="loader1"></div>
      </div>

      <div v-else class="profile-doctor profile-doctor-container">
        <div class="doctor-container ">
          <DoctorImageSection
            :doctor="doctor"
            :loading="loading"
            @update:doctor="updateDoctor"
            @show-toast="handleToast"
          />

          <div class="doctor-details">
            <DoctorTabsHeader
              :route-id="routeId"
              :search="search"
              @find-doctor="findDoctor"
              @update:search="updateSearch"
              @tab-change="activeTab = $event"
            />

            <div class="tab-content" id="myTabContent">
              <!-- Profile Tab -->
              <div
                class="tab-pane fade show active"
                id="profile"
                role="tabpanel"
              >
                <DoctorProfileForm
                  :doctor="doctor"
                  :errors="errors"
                  :route-id="routeId"
                  @update:doctor="updateDoctor"
                />
              </div>

              <!-- Summary Tab -->
              <div class="tab-pane fade show" id="summary" role="tabpanel">
                <DoctorSummaryForm
                  :doctor="doctor"
                  :errors="errors"
                  @update:doctor="updateDoctor"
                />
              </div>

              <!-- Timeslots Tab -->
              <div class="tab-pane fade" id="timeslot" role="tabpanel">
                <DoctorTimeslotsForm
                  ref="timeSlotsForm"
                  :doctor="doctor"
                  :errors="errors"
                  :selected-day="selectedDay"
                  :selected-days="selectedDays"
                  @update:selected-day="selectedDay = $event"
                  @update:selected-days="selectedDays = $event"
                  @generate-slots="generateSlots"
                  @remove-slot="removeSlot"
                  @update:doctor="updateDoctor"
                  @delete-day-slots="deleteDaySlots"
                  @show-toast="handleToast"
                  @step-change="activeStep = $event"
                />
              </div>
            </div>

            <div v-if="shouldShowSaveButton" class="profile-save-btn">
              <button
                @click="saveDoctor"
                class="save-btn"
                :disabled="isLoading"
              >
                Save
                <span
                  v-if="isLoading"
                  class="spinner-border spinner-border-sm"
                  role="status"
                ></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <FooterView />
    </div>
  </div>
</template>

<script>
import { DoctorsService } from "@/services/doctors.service";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Tab } from "bootstrap";
import FooterView from "@/layout/Footer.vue";
import Header from "@/layout/Header.vue";
import DoctorImageSection from "@/components/doctorprofile/DoctorImageSection.vue";
import DoctorTabsHeader from "@/components/doctorprofile/DoctorTabsHeader.vue";
import DoctorProfileForm from "@/components/doctorprofile/DoctorProfileForm.vue";
import DoctorSummaryForm from "@/components/doctorprofile/DoctorSummaryForm.vue";
import DoctorTimeslotsForm from "@/components/doctorprofile/DoctorTimeslotsForm.vue";
import { getCoreDigits, isCanadaHospital, lowercaseString, validatePhone } from "@/utils/helpers";

dayjs.extend(utc);
dayjs.extend(timezone);
export default {
  name: "DoctorProfile",
  components: {
    FooterView,
    Header,
    DoctorImageSection,
    DoctorTabsHeader,
    DoctorProfileForm,
    DoctorSummaryForm,
    DoctorTimeslotsForm,
  },
  data() {
    return {
      doctor: {
        firstname: "",
        lastname: "",
        speciality: "",
        departmentCode: "",
        aboutMe: "",
        experienceYears: 0,
        gender: "",
        medicalDegree: "",
        languagesSpoken: [],
        phone: "",
        hospitalId: this.$store.state.user.currentUser.objectId,
        email: "",
        photo: "",
        consultationFee: null,
        workingDays: "",
        sittingTime: "",
        reference: "",
        startDate: "",
        endDate: "",
        days: [
          {
            day: "Monday",
            numberOfPatients: 0,
            break: 0,
            slots: [],
            appointmentType: "",
          },
          {
            day: "Tuesday",
            numberOfPatients: 0,
            break: 0,
            slots: [],
            appointmentType: "",
          },
          {
            day: "Wednesday",
            numberOfPatients: 0,
            break: 0,
            slots: [],
            appointmentType: "",
          },
          {
            day: "Thursday",
            numberOfPatients: 0,
            break: 0,
            slots: [],
            appointmentType: "",
          },
          {
            day: "Friday",
            numberOfPatients: 0,
            break: 0,
            slots: [],
            appointmentType: "",
          },
          {
            day: "Saturday",
            numberOfPatients: 0,
            break: 0,
            slots: [],
            appointmentType: "",
          },
          {
            day: "Sunday",
            numberOfPatients: 0,
            break: 0,
            slots: [],
            appointmentType: "",
          },
        ],
        enabledDays: [],
      },
      search: {
        email: "",
        phone: "",
        error: "",
      },
      selectedDay: "Monday",
      selectedDays: [],
      errors: {},
      isLoading: false,
      pageLoading: false,
      loading: false,
      routeId: "",
      activeTab: "profile",
      activeStep: 0,
    };
  },
  computed: {
    currentDay() {
      if (!this.selectedDay || !this.doctor?.days?.length) return null;
      const selectedType =
        this.$refs?.timeSlotsForm?.selectedAppointmentType || "";
      return (
        this.doctor.days.find(
          (day) =>
            day.day === this.selectedDay &&
            (!selectedType || day.appointmentType === selectedType)
        ) || null
      );
    },
    isCanadaHospital() {
      return isCanadaHospital();
    },
    shouldShowSaveButton() {
      // Hide completely on timeslot tab
      if (this.activeTab === 'timeslot') {
        return false;
      }
      return true;
    }
  },
  methods: {
    handleToast({ type, message }) {
      this.$store.commit("toast/NEW", { type, message });
    },
    // Strips country prefix and returns the 10-digit core number
    getCoreDigits(phone, isCanada) {
      return getCoreDigits(phone, isCanada);
    },
    deleteDaySlots(dayName) {
      const day = this.doctor.days.find((d) => d.day === dayName);
      if (day) {
        if (
          confirm(`Are you sure you want to delete all slots for ${dayName}?`)
        ) {
          day.startTime = "";
          day.endTime = "";
          day.numberOfPatients = "";
          day.break = "";
          day.slots = [];
        }
      }
    },
    updateDoctor(updatedData) {
      if (updatedData.days) {
        // 🧹 Filter out any invalid, blank, or deleted days
        const sanitizedDays = updatedData.days.filter(
          (d) =>
            d.day &&
            d.day.trim() !== "" &&
            d.appointmentType &&
            d.appointmentType.trim() !== ""
        );

        // Deep merge: keep unique (day + appointmentType)
        const mergedDays = [];

        sanitizedDays.forEach((newDay) => {
          const existingIndex = mergedDays.findIndex(
            (d) =>
              d.day === newDay.day &&
              d.appointmentType === newDay.appointmentType
          );

          if (existingIndex !== -1) {
            mergedDays[existingIndex] = {
              ...mergedDays[existingIndex],
              ...newDay,
            };
          } else {
            mergedDays.push(newDay);
          }
        });

        // ✅ Assign cleaned days back
        this.doctor.days = mergedDays;
      }

      // Merge other top-level fields safely
      this.doctor = { ...this.doctor, ...updatedData, days: this.doctor.days };
    },

    updateSearch(updatedSearch) {
      this.search = { ...this.search, ...updatedSearch };
    },

    isUTC(time) {
      if (!time) return false;
      return time.endsWith("Z") || time.includes("T");
    },

    removeSlot(index) {
      if (!this.currentDay) return;
      this.currentDay.slots.splice(index, 1);
    },

    generateSlots() {
      this.errors = {};

      // Find the appointment type for the selected day
      // ✅ Always get the selected appointment type from the child component
      const selectedType =
        this.$refs?.timeSlotsForm?.selectedAppointmentType || "";

      if (!selectedType) {
        this.errors[`${this.selectedDay}_appointmentType`] =
          "Select an appointment type first.";
        return;
      }

      // ✅ Find or create entry for selected day + type
      let targetDay = this.doctor.days.find(
        (d) => d.day === this.selectedDay && d.appointmentType === selectedType
      );

      if (!targetDay) {
        targetDay = {
          day: this.selectedDay,
          appointmentType: selectedType,
          numberOfPatients: 0,
          break: 0,
          slots: [],
          startTime: "",
          endTime: "",
        };
        this.doctor.days.push(targetDay);
      }

      const {
        startTime,
        endTime,
        numberOfPatients,
        break: breakTime,
      } = targetDay;

      if (!startTime || !endTime || numberOfPatients <= 0) {
        this.errors[
          `${this.selectedDay}_numberOfPatients`
        ] = `Please enter valid start time, end time, and at least 1 patient.`;
        return;
      }

      const startMinutes = this.timeToMinutes(startTime);
      const endMinutes = this.timeToMinutes(endTime);
      const totalAvailableMinutes = endMinutes - startMinutes;
      if (totalAvailableMinutes <= 0) {
        this.errors[
          `${this.selectedDay}_numberOfPatients`
        ] = `End time must be greater than start time.`;
        return;
      }

      const totalBreakTime = (numberOfPatients - 1) * breakTime;
      const availableSlotTime = totalAvailableMinutes - totalBreakTime;
      if (availableSlotTime <= 0) {
        this.errors[
          `${this.selectedDay}_numberOfPatients`
        ] = `Not enough time to schedule ${numberOfPatients} patients.`;
        return;
      }

      const slotDuration = Math.floor(availableSlotTime / numberOfPatients);
      let currentTime = startMinutes;
      targetDay.slots = [];

      for (let i = 0; i < numberOfPatients; i++) {
        if (currentTime >= endMinutes) break;
        const slotStart = this.minutesToTime(currentTime);
        currentTime += slotDuration;
        if (i === numberOfPatients - 1) {
          currentTime = Math.min(currentTime, endMinutes);
        }
        const slotEnd = this.minutesToTime(currentTime);
        targetDay.slots.push({ startTime: slotStart, endTime: slotEnd });
        if (i < numberOfPatients - 1) currentTime += breakTime;
      }

      // update doctor state
      this.updateDoctor({ days: [...this.doctor.days] });
    },

    convertTo12Hour(time24) {
      let [hours, minutes] = time24.split(":").map(Number);
      let period = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${minutes?.toString()?.padStart(2, "0")} ${period}`;
    },

    convertToLocalTime(utcTime, format = 12) {
      if (!utcTime || typeof utcTime !== "string") return "";

      try {
        // Ensure we have HH:mm format
        const [hours, minutes] = utcTime.split(":").map(Number);
        if (isNaN(hours) || isNaN(minutes)) return "";

        // Build a UTC-based date safely and convert to local
        const now = dayjs.utc().hour(hours).minute(minutes);
        const local = now.local();

        return local.format(format === 12 ? "hh:mm A" : "HH:mm");
      } catch (err) {
        console.error("⚠️ convertToLocalTime error:", utcTime, err);
        return "";
      }
    },

    convertToLocalDate(utcDate) {
      if (!utcDate) return "";
      return dayjs(utcDate).local().format("YYYY-MM-DD");
    },

    timeToMinutes(time) {
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    },

    minutesToTime(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}`;
    },

    formatTime(time) {
      if (!time) return "";
      const [hour, minute] = time.split(":").map(Number);
      const ampm = hour >= 12 ? "PM" : "AM";
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    },

    validateForm() {
      this.errors = {};
      let firstErrorTab = null;

      // Time Slots Tab validations
      if (this.currentDay && this.currentDay.numberOfPatients < 0) {
        this.errors[
          `${this.selectedDay}_numberOfPatients`
        ] = `Number of patients cannot be negative.`;
        if (!firstErrorTab) firstErrorTab = "timeslot";
      }

      if (this.currentDay && this.currentDay.break < 0) {
        this.errors[
          `${this.selectedDay}_break`
        ] = `Break time cannot be negative.`;
        if (!firstErrorTab) firstErrorTab = "timeslot";
      }

      // Profile Tab validations
      if (!this.doctor.firstname) {
        this.errors.firstname = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }
      if (!this.doctor.lastname) {
        this.errors.lastname = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }
      if (!this.doctor.speciality) {
        this.errors.speciality = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }
      if (!this.doctor.aboutMe) {
        this.errors.aboutMe = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }
      if (!this.doctor.gender) {
        this.errors.gender = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }

      {
        const phoneResult = validatePhone(String(this.doctor.phone || ''));
        if (!phoneResult.valid) {
          this.errors.phone = phoneResult.message;
          if (!firstErrorTab) firstErrorTab = "profile";
        }
      }

      if (!this.doctor.experienceYears) {
        this.errors.experienceYears = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }

      if (!this.doctor.medicalDegree) {
        this.errors.medicalDegree = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }

      if (!this.doctor.languagesSpoken.length) {
        this.errors.languagesSpoken = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      }

      if (!this.$route.query.id) {
        if (!this.doctor.email) {
          this.errors.email = "This field is required.";
          if (!firstErrorTab) firstErrorTab = "profile";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.doctor.email)) {
          this.errors.email = "Please enter a valid email address.";
          if (!firstErrorTab) firstErrorTab = "profile";
        }
      }

      if (!this.doctor.departmentCode) {
        this.errors.departmentCode = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "profile";
      } else if (!/^[A-Z]{2}$/.test(this.doctor.departmentCode)) {
        this.errors.departmentCode =
          "Must be exactly two uppercase letters (e.g., AB, DJ).";
        if (!firstErrorTab) firstErrorTab = "profile";
      }

      // Summary Tab validations
      if (!this.doctor.consultationFee) {
        this.errors.consultationFee = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "summary";
      }
      if (!this.doctor.workingDays) {
        this.errors.workingDays = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "summary";
      }
      if (!this.doctor.sittingTime) {
        this.errors.sittingTime = "This field is required.";
        if (!firstErrorTab) firstErrorTab = "summary";
      }

      this.doctor.days.forEach((day) => {
        if (day.startTime || day.endTime || day.numberOfPatients > 0) {
          if (!this.doctor.startDate) {
            this.errors.startDate = "This field is required.";
            if (!firstErrorTab) firstErrorTab = "timeslot";
          }
          if (!this.doctor.endDate) {
            this.errors.endDate = "This field is required.";
            if (!firstErrorTab) firstErrorTab = "timeslot";
          }
          if (!day.startTime) {
            this.errors[
              `${day.day}_startTime`
            ] = `${day.day}: Start time is required.`;
            if (!firstErrorTab) firstErrorTab = "timeslot";
          }
          if (!day.endTime) {
            this.errors[
              `${day.day}_endTime`
            ] = `${day.day}: End time is required.`;
            if (!firstErrorTab) firstErrorTab = "timeslot";
          }
          if (!day.numberOfPatients && (!day.slots || day.slots.length === 0)) {
            this.errors[
              `${day.day}_numberOfPatients`
            ] = `${day.day}: Number of patients is required for auto-generated slots.`;
            if (!firstErrorTab) firstErrorTab = "timeslot";
          }
          if (
            day.startTime &&
            day.endTime &&
            day.numberOfPatients > 0 &&
            day.slots.length === 0
          ) {
            this.errors[`${day.day}_slots`] = `${day.day}: Slots were missing.`;
            if (!firstErrorTab) firstErrorTab = "timeslot";
          }
        }
      });

      // Auto-switch to tab with first error
      if (firstErrorTab) {
        const tabTrigger = document.querySelector(`#${firstErrorTab}-tab`);
        if (tabTrigger) {
          new Tab(tabTrigger).show();
        }
      }

      return Object.keys(this.errors).length === 0;
    },
    async saveDoctor() {
      try {
        this.isLoading = true;
        console.log("🔍 Save button clicked — starting saveDoctor()");
        if (!this.validateForm()) {
          console.warn("⚠️ Validation failed — exiting before API call");
          return;
        }

        // --- Normalize and sanitize data ---
        const userOffset = dayjs().utcOffset(); // ✅ moved up here

        const formattedDoctor = {
          ...this.doctor,
          ...(this.$route.query.id ? { email: undefined } : {}),
          phone: String(this.doctor.phone).replace(/\D/g, ""),
          experienceYears: Number(this.doctor.experienceYears) || 0,
          consultationFee: Number(this.doctor.consultationFee) || 0,
          ...(this.doctor.email && { email: lowercaseString(this.doctor.email) }),

          // Convert dates to ISO UTC format
          startDate: this.doctor.startDate
            ? dayjs(this.doctor.startDate)
                .startOf("day")
                .utc(true)
                .toISOString()
            : null,
          endDate: this.doctor.endDate
            ? dayjs(this.doctor.endDate).startOf("day").utc(true).toISOString()
            : null,

          // ✅ FIXED TIMEZONE CONVERSION BLOCK
          days: this.doctor.days.map((day) => {
            const baseDate = dayjs(this.doctor.startDate);

            // Convert entered local times using fixed offset (no DST shift)
            const startUtc = day.startTime
              ? dayjs(`${baseDate.format("YYYY-MM-DD")}T${day.startTime}`)
                  .utcOffset(userOffset, true)
                  .utc()
                  .format("HH:mm")
              : null;

            const endUtc = day.endTime
              ? dayjs(`${baseDate.format("YYYY-MM-DD")}T${day.endTime}`)
                  .utcOffset(userOffset, true)
                  .utc()
                  .format("HH:mm")
              : null;

            const slots = Array.isArray(day.slots)
              ? day.slots.map((slot) => ({
                  startTime: dayjs(
                    `${baseDate.format("YYYY-MM-DD")}T${slot.startTime}`
                  )
                    .utcOffset(userOffset, true)
                    .utc()
                    .format("HH:mm"),
                  endTime: dayjs(
                    `${baseDate.format("YYYY-MM-DD")}T${slot.endTime}`
                  )
                    .utcOffset(userOffset, true)
                    .utc()
                    .format("HH:mm"),
                }))
              : [];

            return {
              ...day,
              numberOfPatients: Number(day.numberOfPatients) || 0,
              break: Number(day.break) || 0,
              appointmentType: day.appointmentType || null,
              startTime: startUtc,
              endTime: endUtc,
              slots,
            };
          }),
        };

        // 🧹 Clean up empty or invalid day entries
        formattedDoctor.days = formattedDoctor.days.filter(
          (d) =>
            d.day &&
            d.day.trim() !== "" &&
            d.appointmentType &&
            d.appointmentType.trim() !== "" &&
            d.startTime &&
            d.endTime
        );

        // --- API call ---
        const response = await DoctorsService.create(formattedDoctor);

        if (response.success) {
          try {
            // const created = response.data || {};
            // const doctorId = created.id || created.doctorId || formattedDoctor.id || formattedDoctor.doctorId || null;
            // const hospitalId = formattedDoctor.hospitalId || this.$store.state.user.currentUser.objectId || null;
            // const childSelected = this.$refs?.timeSlotsForm?.selectedDays;
            // const selectedFromUi = Array.isArray(childSelected) && childSelected.length ? childSelected : (Array.isArray(this.selectedDays) && this.selectedDays.length ? this.selectedDays : null);
            // const days = selectedFromUi || (Array.isArray(formattedDoctor.days) ? [...new Set(formattedDoctor.days.map(d => d.day).filter(Boolean))] : []);

            // if (doctorId && hospitalId && days && days.length) {
            //   const payload = { doctorId, hospitalId, days };
            //   const sitResp = await DoctorTimeSlotService.updateSittingDates(payload);
            //   if (!(sitResp && sitResp.success)) {
            //     this.$store.commit("toast/NEW", { type: "error", message: sitResp?.message || "Failed to update enabled days." });
            //   }
            // }

            this.$store.commit("toast/NEW", { type: "success", message: "Doctor saved successfully!" });
          } catch (e) {
            console.error("Error updating sitting dates:", e);
            this.$store.commit("toast/NEW", { type: "error", message: "Failed to update enabled days." });
          }

          await new Promise((resolve) => setTimeout(resolve, 1000));
          this.$store.commit("toast/NEW", {
            type: "success",
            message: response.message || "Doctor saved successfully!",
          });
          this.$router.push("/hospital-settings");
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: response.message || "Failed to save doctor details.",
          });
        }
      } catch (error) {
        console.error("Error saving doctor:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: "Something went wrong while saving the doctor.",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async findDoctor() {
      try {
        this.errors = {};
        this.search.error = "";
        if (!this.search.email && !this.search.phone) {
          this.search.error = "Either email or phone is required.";
          return;
        }

        this.pageLoading = true;
        const response = await DoctorsService.findDoctor({
          email: lowercaseString(this.search.email),
          phone: this.search.phone,
          hospitalId: this.$store.state.user.currentUser.objectId,
        });

        if (response.success) {
          this.setDoctorDetails(response?.data);
        } else {
          this.search.error = response.message;
          this.setDoctorDetails(null);
        }
      } catch (e) {
        console.log("🚀 ~ findDoctor ~ e:", e);
      } finally {
        this.pageLoading = false;
      }
    },

    setDoctorDetails(doctor) {
      if (doctor) {
        if (doctor.hospitalId == this.$store.state.user.currentUser.objectId) {
          const timeSlotsDays = doctor?.timeSlots?.days || [];

          // Preserve all day + appointmentType combinations
          this.doctor.days = timeSlotsDays.length
            ? timeSlotsDays.map((d) => ({
                day: d.day,
                appointmentType: d.appointmentType || "",
                numberOfPatients: d.numberOfPatients || 0,
                break: d.break || 0,
                startTime: this.convertToLocalTime(d.startTime, 24),
                endTime: this.convertToLocalTime(d.endTime, 24),
                slots: Array.isArray(d.slots)
                  ? d.slots.map((slot) => {
                      console.log("🔍 Slot being processed:", slot);
                      return {
                        startTime: this.convertToLocalTime(slot.startTime, 24),
                        endTime: this.convertToLocalTime(slot.endTime, 24),
                      };
                    })
                  : [],
              }))
            : this.doctor.days;
        }

        this.doctor.firstname = doctor?.firstname;
        this.doctor.lastname = doctor?.lastname;
        this.doctor.speciality = doctor?.speciality;
        this.doctor.departmentCode = doctor?.departmentCode;
        this.doctor.email = lowercaseString(doctor?.email);
        this.doctor.phone = doctor?.phone;
        this.doctor.aboutMe = doctor?.aboutMe;
        this.doctor.experienceYears = doctor?.experienceYears;
        this.doctor.gender = doctor?.gender;
        this.doctor.languagesSpoken = doctor?.languagesSpoken || [];
        this.doctor.medicalDegree = doctor?.medicalDegree;
        this.doctor.sittingTime = doctor?.timeSlots?.sittingTime;
        this.doctor.workingDays = doctor?.timeSlots?.workingDays;
        this.doctor.consultationFee = doctor?.timeSlots?.consultationFee;
        this.doctor.photo = doctor?.photo;
        this.doctor.doctorId = doctor?.id;

        this.doctor.startDate = this.convertToLocalDate(
          doctor?.timeSlots?.startDate
        );
        this.doctor.endDate = this.convertToLocalDate(
          doctor?.timeSlots?.endDate
        );
        this.doctor.enabledDays = doctor?.timeSlots?.enabledDays;
      } else {
        // Reset doctor data
        this.doctor = {
          firstname: "",
          lastname: "",
          speciality: "",
          departmentCode: "",
          aboutMe: "",
          experienceYears: 0,
          gender: "",
          medicalDegree: "",
          languagesSpoken: [],
          phone: "",
          hospitalId: this.$store.state.user.currentUser.objectId,
          email: "",
          photo: "",
          consultationFee: null,
          workingDays: "",
          sittingTime: "",
          startDate: "",
          endDate: "",
          days: [
            {
              day: "Monday",
              numberOfPatients: 0,
              break: 0,
              slots: [],
              appointmentType: "",
            },
            {
              day: "Tuesday",
              numberOfPatients: 0,
              break: 0,
              slots: [],
              appointmentType: "",
            },
            {
              day: "Wednesday",
              numberOfPatients: 0,
              break: 0,
              slots: [],
              appointmentType: "",
            },
            {
              day: "Thursday",
              numberOfPatients: 0,
              break: 0,
              slots: [],
              appointmentType: "",
            },
            {
              day: "Friday",
              numberOfPatients: 0,
              break: 0,
              slots: [],
              appointmentType: "",
            },
            {
              day: "Saturday",
              numberOfPatients: 0,
              break: 0,
              slots: [],
              appointmentType: "",
            },
            {
              day: "Sunday",
              numberOfPatients: 0,
              break: 0,
              slots: [],
              appointmentType: "",
            },
          ],
        };
      }
    },
  },
  async created() {
    const id = this.$route.query.id;
    this.routeId = this.$route.query.id;
    if (id) {
      try {
        this.pageLoading = true;
        const response = await DoctorsService.getById(id, {
          hospitalId: this.$store.state.user.currentUser.objectId,
        });
        if (response.success) {
          this.setDoctorDetails(response.data);
        }
      } catch (e) {
        console.log("🚀 ~ created ~ e:", e);
      } finally {
        this.pageLoading = false;
      }
    }
  },
};
</script>

<style scoped>
.doc-profile-main {
  min-height: 100vh;
  height: auto;
}
.doc-profile-main .profile-doctor {
  margin-top: 170px;
  margin-bottom: 70px;
}
.tab-content {
  margin-top: 10px;
}

.profile-save-btn {
  margin-top: 20px;
}

.page-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.loader1 {
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin1 1s linear infinite;
}

@keyframes spin1 {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.save-btn {
  padding: 10px 80px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.profile-doctor-container {
  width: 80%;
  margin: 0 auto;
}

</style>
